import { type HTMLAttributes, type ReactNode } from 'react';

import { parseClassnames } from '@incloodsolutions/react-toolkit';

type CardVariant = 'default' | 'dashed' | 'soft';
type CardSize = 'default' | 'compact';

interface ICardLayoutComponentProps extends HTMLAttributes<HTMLDivElement> {
	variant?: CardVariant;
	/** `compact` is the smaller corner radius, for a card sitting inside another surface such as a modal or a form. */
	size?: CardSize;
	className?: string;
	children: ReactNode;
}

/**
 * The standard surface for grouped content in all Workinance apps. The base look (rounded, bordered,
 * panel colour) has no padding, so pass spacing and layout through `className`, e.g. className="p-6".
 * Use variant="dashed" for empty-state and placeholder panels, variant="soft" for a tinted callout.
 */
export function CardLayoutComponent({
	variant = 'default',
	size = 'default',
	className,
	children,
	...divProps
}: ICardLayoutComponentProps) {
	return (
		<div
			{...divProps}
			className={parseClassnames(
				'border',
				{
					'rounded-[20px]': size === 'default',
					'rounded-2xl': size === 'compact',
					'border-rule bg-panel': variant === 'default',
					'border-dashed border-edge/50': variant === 'dashed',
					'border-rule bg-brand-soft/60': variant === 'soft',
				},
				className,
			)}
		>
			{children}
		</div>
	);
}
